import {observer} from 'mobx-react-lite'
import React, {useCallback, useMemo, useRef, useState} from 'react'
import {Lang} from 'yougile-lang'
import {Tracking, TrackingEvent} from '@common/tracking'
import {Menu, MenuItemData} from '@components/common/base/menu'
import {ProjectCardAdd} from '@components/common/base/project-card'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {crmStore} from '@components/crm/crm-store'
import {
  CompanyProjectsStore,
  useCompanyProjectsStore
} from '../company-projects-store'

const useProjectTypesMenu = (
  store: CompanyProjectsStore
): {
  isMenuOpened: boolean
  openMenu: () => void
  closeMenu: () => void
  menuItems: MenuItemData[]
} => {
  const [isMenuOpened, setIsMenuOpened] = useState(false)
  const openMenu = useCallback(() => setIsMenuOpened(true), [setIsMenuOpened])
  const closeMenu = useCallback(() => setIsMenuOpened(false), [setIsMenuOpened])

  const menuItems = useMemo<MenuItemData[]>(() => {
    const addDefaultProjectItem: MenuItemData = {
      id: 'add-default-project',
      title: Lang.msg.addProjectWithTasks,
      icon: 'IconPlus',
      onClick: async e => {
        await store.newProjOpen(e)
      }
    }

    const addCrmProjectItem: MenuItemData = {
      id: 'add-crm-project',
      title: Lang.msg.crm.addProject,
      icon: 'IconPlus',
      onClick: () => {
        Tracking.send(TrackingEvent.crmClickOnAddCrmProjectButtonInDropdown)
        store.newCrmProjOpen()
      }
    }

    return [addDefaultProjectItem, addCrmProjectItem]
  }, [store])

  return {isMenuOpened, openMenu, closeMenu, menuItems}
}

export const AddProjectButton = observer(() => {
  const [, store] = useCompanyProjectsStore()
  const menuBtnRef = useRef(null)
  const {menuItems, isMenuOpened, openMenu, closeMenu} =
    useProjectTypesMenu(store)

  const hasMultipleProjectTypes = crmStore.isCrmEnabled

  if (!hasMultipleProjectTypes) {
    return <ProjectCardAdd onClick={store.newProjOpen} />
  }

  return (
    <>
      <ProjectCardAdd ref={menuBtnRef} onClick={openMenu} />
      {isMenuOpened && (
        <Dropdown
          reference={menuBtnRef}
          placement="bottom"
          hideArrow
          visible
          onVisibleChange={closeMenu}
          theme="new-menu"
          content={<Menu items={menuItems} onItemClicked={closeMenu} />}
        />
      )}
    </>
  )
})
